import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CalendarDays, BookOpen, Heart, Clock, MapPin, UserPlus,
  ArrowRight, Users, Radio, BookmarkCheck, Share2, Download, Sparkles,
} from "lucide-react";
import axios from "axios";
import { SplashScreen } from "@/components/SplashScreen";
import { VerseSkeleton } from "@/components/Skeletons";
import HeroSection from "@/components/sections/HeroSection";
import VerseOfTheDay from "@/components/sections/VerseOfTheDay";
import EventsCarousel from "@/components/sections/EventsCarousel";
import CollectivesTeaser from "@/components/sections/CollectivesTeaser";
import HomeFeatureBanner from "@/components/sections/HomeFeatureBanner";
import BookstoreTeaser from "@/components/sections/BookstoreTeaser";
import GivingTeaser from "@/components/sections/GivingTeaser";
import LocationAndFooter from "@/components/sections/LocationAndFooter";

import API from "@/apiConfig";
const BOOK_IMG = "https://assets.wordaliveglobal.org/products/Nissan(FirstFruits)2026-4.jpg";

const DAILY_VERSE_REFS = [
  "john+3:16", "jeremiah+29:11", "philippians+4:13", "romans+8:28",
  "isaiah+40:31", "psalm+23:1", "matthew+6:33", "joshua+1:9",
  "proverbs+3:5-6", "2+corinthians+5:17", "hebrews+11:1", "ephesians+2:8-9",
  "romans+12:2", "hebrews+4:12", "james+1:17", "john+15:5",
  "matthew+11:28-30", "psalm+46:10", "isaiah+41:10", "lamentations+3:22-23",
  "proverbs+18:10", "romans+5:8", "1+corinthians+13:13", "psalm+91:1-2",
  "matthew+5:14-16", "acts+1:8", "philippians+4:6-7", "romans+8:38-39",
  "revelation+21:4", "psalm+119:105",
];

function filterNext45Days(events) {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() + 45);
  return events.filter((e) => {
    const [y, m, d] = e.date.split("-");
    const eventDate = new Date(Number(y), Number(m) - 1, Number(d));
    return eventDate >= now && eventDate <= cutoff;
  });
}

export default function HomePage() {
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [showSplash, setShowSplash] = useState(false);
  const [verse, setVerse] = useState(null);
  const [verseLoading, setVerseLoading] = useState(true);
  const [isLive, setIsLive] = useState(false);
  const [liveTitle, setLiveTitle] = useState("");
  const [liveId, setLiveId] = useState("");
  const [profile, setProfile] = useState(null);
  const [showElishaBadge, setShowElishaBadge] = useState(false);

  useEffect(() => {
    const hasSeenSplash = sessionStorage.getItem("hasSeenSplash");
    if (!hasSeenSplash) setShowSplash(true);

    // Load profile from localStorage
    const saved = localStorage.getItem("wagc_profile");
    if (saved) setProfile(JSON.parse(saved));

    // Elisha Times Badge — show only if a real issue was published within the last 7 days
    fetch(`${API}/elisha_times`)
      .then(r => r.json())
      .then(data => {
        const now = Date.now();
        const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
        const hasFresh = (data.issues || []).some(i => {
          const pub = new Date(i.publish_date).getTime();
          return now - pub <= sevenDaysMs;
        });
        setShowElishaBadge(hasFresh);
      })
      .catch(() => setShowElishaBadge(false));
  }, []);

  const handleSplashComplete = () => {
    setShowSplash(false);
    sessionStorage.setItem("hasSeenSplash", "true");
  };

  useEffect(() => {
    axios.get(`${API}/events`).then((r) => setEvents(filterNext45Days(r.data))).catch(() => {}).finally(() => setEventsLoading(false));
    axios.get(`${API}/products?limit=8`).then((r) => {
      // Products API returns { products: [...] }
      if (r.data && r.data.products) {
        setProducts(r.data.products.slice(0, 4));
      } else if (Array.isArray(r.data)) {
        setProducts(r.data.slice(0, 4));
      }
    }).catch(() => {});

    // Fetch daily Bible verse
    const dayOfYear = Math.floor(
      (Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000
    );
    const ref = DAILY_VERSE_REFS[dayOfYear % DAILY_VERSE_REFS.length];
    fetch(`https://bible-api.com/${ref}?translation=kjv`)
      .then((r) => r.json())
      .then((data) => setVerse(data))
      .catch(() =>
        setVerse({
          reference: "Psalm 46:10",
          text: "Be still, and know that I am God: I will be exalted among the heathen, I will be exalted in the earth.",
        })
      )
      .finally(() => setVerseLoading(false));

  // Check for live stream
    fetch(`${API}/youtube/videos`)
      .then((r) => r.json())
      .then((data) => {
        if (data.isLive) {
          setIsLive(true);
          const live = data.videos?.find((v) => v.isLive);
          if (live) {
            setLiveTitle(live.title);
            setLiveId(live.id);
          }
        }
      })
      .catch(() => {});
  }, []);

  const shareVerseImage = async () => {
    if (!verse) return;
    const W = 1080, H = 1080;
    const canvas = document.createElement("canvas");
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d");

    // Navy background
    ctx.fillStyle = "#0A1A2F";
    ctx.fillRect(0, 0, W, H);

    // Subtle gold border
    ctx.strokeStyle = "#D4AF37";
    ctx.lineWidth = 6;
    ctx.strokeRect(40, 40, W - 80, H - 80);

    // Decorative corner accents
    const accentLen = 60;
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#D4AF37";
    [[44, 44, 1, 1], [W - 44, 44, -1, 1], [44, H - 44, 1, -1], [W - 44, H - 44, -1, -1]].forEach(([x, y, dx, dy]) => {
      ctx.beginPath();
      ctx.moveTo(x, y + dy * accentLen);
      ctx.lineTo(x, y);
      ctx.lineTo(x + dx * accentLen, y);
      ctx.stroke();
    });

    // Gold divider line top
    ctx.strokeStyle = "rgba(212, 175, 55, 0.5)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(W / 2 - 80, 160);
    ctx.lineTo(W / 2 + 80, 160);
    ctx.stroke();

    // "Verse of the Day" label
    ctx.fillStyle = "#D4AF37";
    ctx.font = "600 16px Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.letterSpacing = "4px";
    ctx.fillText("VERSE OF THE DAY", W / 2, 140);

    // Verse text — word wrap
    const verseText = `"${verse.text?.trim()}"`;
    const maxWidth = W - 200;
    ctx.fillStyle = "rgba(255, 255, 255, 0.92)";
    ctx.font = "italic 600 36px Georgia, serif";
    ctx.textAlign = "center";

    const words = verseText.split(" ");
    const lines = [];
    let currentLine = "";
    for (const word of words) {
      const testLine = currentLine ? currentLine + " " + word : word;
      if (ctx.measureText(testLine).width > maxWidth) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lines.push(currentLine);

    const lineHeight = 50;
    const totalTextHeight = lines.length * lineHeight;
    const startY = (H / 2) - (totalTextHeight / 2) + 20;

    lines.forEach((line, i) => {
      ctx.fillText(line, W / 2, startY + i * lineHeight);
    });

    // Reference
    const refY = startY + totalTextHeight + 30;
    ctx.fillStyle = "#D4AF37";
    ctx.font = "bold 28px Arial, sans-serif";
    ctx.fillText(`— ${verse.reference}`, W / 2, refY);

    // Gold divider line bottom
    ctx.strokeStyle = "rgba(212, 175, 55, 0.5)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(W / 2 - 80, refY + 30);
    ctx.lineTo(W / 2 + 80, refY + 30);
    ctx.stroke();

    // Church name at bottom
    ctx.fillStyle = "rgba(212, 175, 55, 0.7)";
    ctx.font = "600 18px Arial, sans-serif";
    ctx.fillText("Word Alive Global Center", W / 2, H - 100);
    ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
    ctx.font = "14px Arial, sans-serif";
    ctx.fillText("Carthage, Texas  |  wordaliveglobal.org", W / 2, H - 74);

    // Try loading logo
    try {
      const logo = new Image();
      logo.crossOrigin = "anonymous";
      logo.src = "https://assets.wordaliveglobal.org/branding/icon-192.png";
      await new Promise((resolve, reject) => {
        logo.onload = resolve;
        logo.onerror = reject;
        setTimeout(reject, 2000);
      });
      const logoSize = 60;
      ctx.drawImage(logo, W / 2 - logoSize / 2, 76, logoSize, logoSize);
    } catch {
      // Logo failed to load, continue without it
    }

    // Convert to blob and share/download
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const fileName = `WAGC-Verse-${verse.reference.replace(/\s/g, "-")}.png`;

      // Try native share first (mobile)
      if (navigator.share && navigator.canShare) {
        try {
          const file = new File([blob], fileName, { type: "image/png" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: `${verse.reference} — Word Alive`,
              text: verse.text?.trim(),
              files: [file],
            });
            return;
          }
        } catch {
          // Share cancelled or failed, fall through to download
        }
      }

      // Fallback: direct download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  return (
    <>
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
      <Layout hideFooter>

      {/* Live Banner */}
      {isLive && (
        <div className="bg-red-600 text-white py-2.5 px-4 flex items-center justify-between gap-3" data-testid="live-banner">
          <div className="flex items-center gap-2 animate-pulse">
            <Radio className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-wider">Live Now</span>
          </div>
          <p className="text-xs text-white/90 flex-1 text-center truncate">{liveTitle}</p>
          <Link to="/live-events">
            <Button size="sm" className="bg-white text-red-600 hover:bg-white/90 text-xs px-3 h-7 rounded-sm">
              Watch
            </Button>
          </Link>
        </div>
      )}

      {/* Hero */}
      <HeroSection 
        profileName={profile?.name} 
        showElishaBadge={showElishaBadge}
        onInstallClick={() => {
          const prompt = window._wagcInstallPrompt;
          if (prompt) {
            prompt.prompt();
            prompt.userChoice.then(() => {
              window._wagcInstallPrompt = null;
            });
          } else {
            window.location.href = "/me";
          }
        }}
      />

      {/* Featured Page Banner (admin-controlled, subtle) */}
      <HomeFeatureBanner />

      {/* Daily Bible Verse */}
      <VerseOfTheDay 
        verse={verse?.text} 
        reference={verse?.reference} 
        isLoading={verseLoading} 
        onShare={shareVerseImage} 
      />

 

      {/* Upcoming Events Carousel */}
      <EventsCarousel events={events} isLoading={eventsLoading} />

      {/* Collectives Teaser */}
      <CollectivesTeaser />

      {/* Bookstore Teaser */}
      <BookstoreTeaser books={products} />

      {/* Give CTA */}
      <GivingTeaser totalGiven="$12,450" recentDonors={87} />

      {/* Location & Unified Footer */}
      <LocationAndFooter />
    </Layout>
    </>
  );
}
